import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import axios from 'axios';
import config from '../../config';
import dayjs from 'dayjs';

import '../../styles/popup.css';
import '../../styles/buttons.css';

import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Tooltip from '@mui/material/Tooltip';
import useAuthenticate from "../authentication/authenticate.js";

import { green } from '@mui/material/colors';
import { List, ListItem } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const ChargeInvoiceForm = ({ onNewInvoice }) => {
  const { user } = useAuthenticate();

  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [accounts, setAccounts] = useState([]);

  const [vendors, setVendors] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [allInvoiceTypes, setAllInvoiceTypes] = useState([]);

  const [activeAccountId, setActiveAccountId] = useState(null); // This will be the account and issuedBy
  const [invoiceData, setInvoiceData] = useState({
    account: null,
    vendor: null,
    invoiceType: '',
    issuedBy: null,
    chargedTo: null,
    dueDate: '',
    isRecurring: false,
    frequency: '',
  });
  const [items, setItems] = useState([]);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [error, setError] = useState('');

  const [activeStep, setActiveStep] = React.useState(0);

  // Set current user
  useEffect(() => {
    const role = Object.keys(user?.active || {})[0]; // 'manager', 'owner', etc.
    const id = user.active[role] ? user.active[role] : null;
    setActiveAccountId(id);
  }, [user.active]);

  useEffect(() => {
    if (!activeAccountId) return; // guard clause
    setInvoiceData((prev) => ({
      ...prev,
      account: activeAccountId,
      issuedBy: activeAccountId,
    }));
  }, [activeAccountId]);

  // Stepper 

  const steps = [
    'Select charged to',
    'Select charged by',
    'Select invoice items',
  ];

  const isStepCompleted = (step) => {
    switch (step) {
      case 0:
        return invoiceData?.chargedTo !== null && invoiceData?.dueDate !== '';
      case 1:
        return Boolean(invoiceData?.vendor);
      case 2:
        return items.length > 0;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      // Reset overlay when finished
      setIsOverlayOpen(false);
      setActiveStep(0);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Fetching properties, units, and accounts

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/v2/property`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });
        const properties = response.data.map(item => ({
          value: item._id,
          label: item.name,
        }));
        setProperties(properties);
      } catch (error) {
        console.error('Error fetching properties:', error);
        setError('Failed to load properties. Please try again.');
      }
    };

    fetchProperties();
  }, []);

  useEffect(() => {
    const fetchUnits = async () => {
      if (!selectedProperty) return;

      try {
        const response = await axios.get(`${config.API_URL}/v2/unit/property/${selectedProperty.value}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });

        const units = response.data.map(item => ({
          value: item._id,
          label: item.unitNumber,
        }));

        setUnits(units);
      } catch (error) {
        console.error('Error fetching units:', error);
        setError('Failed to load units. Please try again.');
      }
    };

    fetchUnits();
  }, [selectedProperty]);

  useEffect(() => {
    if (!selectedUnit) {
      setAccounts([]);
      return;
    }

    const fetchAccountsFromRoles = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/v2/role/unitRole/unit/${selectedUnit.value}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
        }
        );

        console.log('Fetched account from role:', response.data);

        const options = response.data
          .filter(
            (item) =>
              item.propertyRole.role === 'owner' || item.propertyRole.role === 'tenant'
          )
          .map((item) => ({
            value: item.propertyRole.account._id,
            label: `${item.propertyRole.account.user.givenName} ${item.propertyRole.account.user.lastName}`,
          }));

        setAccounts(options);
      } catch (err) {
        console.error('Error fetching roles:', err);
        setError('Failed to load accounts. Please try again.');
      }
    };

    fetchAccountsFromRoles();
  }, [selectedUnit]);

  // Fetching vendors and invoice types

  useEffect(() => {
    const fetchVendorsAndInvoiceTypes = async () => {
      try {
        const [vendorResponse, invoiceTypeResponse] = await Promise.all([
          axios.get(`${config.API_URL}/v2/vendor`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }),
          axios.get(`${config.API_URL}/v2/invoiceType`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }),
        ]);

        setAllVendors(vendorResponse.data);
        setAllInvoiceTypes(invoiceTypeResponse.data);
      } catch (err) {
        console.error("Data fetching error:", err);
      }
    };

    fetchVendorsAndInvoiceTypes()
  }, []);

  useEffect(() => {
    if (!selectedProperty) return;

    const filteredVendors = allVendors.filter(
      (vendor) => vendor?.property?._id === selectedProperty?.value
    );
    const filteredInvoiceTypes = allInvoiceTypes.filter(
      (type) => type?.property?._id === selectedProperty?.value
    );

    setVendors(filteredVendors);
    setInvoiceTypes(filteredInvoiceTypes);

  }, [selectedProperty, allVendors, allInvoiceTypes]);

  // Handle changes

  const handleAddItem = () => {
    setItems([...items, { itemName: '', description: '', amount: 0 }]);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, itemIndex) => itemIndex !== index);
    setItems(newItems);
  };

  const handleCopyItem = (index) => {
    const itemToCopy = items[index];
    const newItem = { ...itemToCopy };
    const newItems = [...items];
    newItems.splice(index + 1, 0, newItem);
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submitting invoice:", invoiceData, items);

    try {
      const selectedVendor = vendors.find(v => v._id === invoiceData.vendor);

      const updatedItems = items.map(item => ({
        ...item,
        unit: selectedUnit.value,
        property: selectedProperty.value
      }));

      const data = {
        invoiceData: {
          ...invoiceData,
          vendor: selectedVendor ? selectedVendor.vendor : '', // Send vendor name
          unit: selectedUnit.value,
          property: selectedProperty.value
        },
        items: updatedItems,
      };

      console.log("Data to be sent:", data);

      const response = await axios.post(
        `${config.API_URL}/v2/invoice`,
        data,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        }
      );

      onNewInvoice(response.data);

      setInvoiceData({
        account: activeAccountId,
        vendor: null,
        invoiceType: null,
        issuedBy: activeAccountId,
        chargedTo: null,
        dueDate: '',
        isRecurring: false,
        frequency: '',
      });
      setItems([]);
      setActiveStep(0);

      console.log('Invoice created successfully:', response.data);
      toast.success('Invoice created successfully!');
      setIsOverlayOpen(false);  // Close overlay after successful submission

    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Failed to create invoice.');
    }
  };

  const resetForm = () => {
    setSelectedProperty(null);
    setSelectedUnit(null);
    setAccounts([]);
    setVendors([]);
    setInvoiceTypes([]);
    setItems([]);
    setActiveStep(0);
    setError('');

    setInvoiceData({
      account: activeAccountId,
      vendor: null,
      invoiceType: '',
      issuedBy: activeAccountId,
      chargedTo: null,
      dueDate: '',
      isRecurring: false,
      frequency: '',
    });
  };

  return (
    <div>
      <div className="bt-button flex flex-col h-[80px] md:flex-row md:w-[130px] md:h-[60px]" onClick={() => setIsOverlayOpen(true)}>
        <i class="fas fa-receipt mr-0 md:mr-[15px]"></i>
        <p className="text-center md:text-left mt-[10px] md:mt-0">Post<span className="hidden md:inline"><br /></span> Invoice</p>
      </div>
      {isOverlayOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <button className="close-popup" onClick={() => {
              setIsOverlayOpen(false);
              resetForm();
            }}>
              <i className="fas fa-times text-black"></i>
            </button>
            <h2 className="text-2xl text-black">Post Invoice</h2>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => {
                const stepProps = {};
                const labelProps = {};
                return (
                  <Step
                    key={label}
                    {...stepProps}
                    sx={{
                      "& .MuiStepIcon-root.Mui-active": {
                        color: "#C86B2C",
                      },
                      "& .MuiStepIcon-root.Mui-completed": {
                        color: "#C86B2C",
                      },
                    }}>
                    <StepLabel {...labelProps}>{label}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
            {activeStep === 0 && (
              <div>
                <h3>Charged To</h3>
                <Autocomplete
                  options={properties}
                  value={selectedProperty}
                  onChange={(event, newValue) => {
                    setSelectedProperty(newValue);
                    setSelectedUnit(null); // Reset selected unit when property changes
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Properties" variant="outlined" fullWidth required />
                  )}
                  sx={{ width: '100%', marginBottom: '1rem' }}
                />
                <Autocomplete
                  options={units}
                  value={selectedUnit}
                  onChange={(event, newValue) => {
                    setSelectedUnit(newValue);
                  }}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField {...params} label="Units" variant="outlined" fullWidth required />
                  )}
                  disabled={!selectedProperty}
                  sx={{ width: '100%', marginBottom: '1rem' }}
                />
                <div className="popup-columns">
                  <Autocomplete
                    options={accounts}
                    value={accounts.find((option) => option.value === invoiceData.chargedTo) || null}
                    onChange={(event, newValue) => {
                      setInvoiceData({ ...invoiceData, chargedTo: newValue ? newValue.value : null });
                    }}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField {...params} label="Charged To" variant="outlined" fullWidth required />
                    )}
                    disabled={!selectedUnit}
                    sx={{ width: '100%' }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Due Date"
                      value={invoiceData.dueDate && dayjs(invoiceData.dueDate).isValid() ? dayjs(invoiceData.dueDate) : null}
                      onChange={(newValue) => {
                        const parsed = dayjs(newValue);
                        setInvoiceData((prevData) => ({
                          ...prevData,
                          dueDate: parsed.isValid() ? parsed.toISOString() : '',
                        }));
                      }}
                      required
                      sx={{ width: '100%' }}
                    />
                  </LocalizationProvider>
                </div>
              </div>
            )}
            {activeStep === 1 && (
              <div>
                <h3>Charged By</h3>
                <Autocomplete
                  options={vendors}
                  getOptionLabel={(option) => option.vendor}
                  value={vendors.find((v) => v._id === invoiceData.vendor) || null}
                  onChange={(e, newValue) =>
                    setInvoiceData((prev) => ({
                      ...prev,
                      vendor: newValue ? newValue._id : '',
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Vendor" variant="outlined" fullWidth required />
                  )}
                  sx={{ width: '100%', marginBottom: '1rem' }}
                  disabled={!selectedProperty}
                />
                <div className="recurring-header">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={invoiceData.isRecurring}
                        onChange={(e) =>
                          setInvoiceData((prevData) => ({
                            ...prevData,
                            isRecurring: e.target.checked, // Update state correctly
                          }))
                        }
                        sx={{
                          '&.Mui-checked': {
                            color: green[600],
                          },
                        }}
                      />
                    }
                    label="Is Recurring"
                    sx={{
                      fontSize: '14px',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '14px',
                      },
                      color: '#000',
                    }}
                  />
                  {invoiceData.isRecurring && (
                    <TextField
                      label="Frequency"
                      variant="outlined"
                      value={invoiceData.frequency}
                      onChange={(e) =>
                        setInvoiceData((prevData) => ({
                          ...prevData,
                          frequency: e.target.value,
                        }))
                      }
                      placeholder="e.g., monthly, yearly"
                      sx={{ width: '48.5%' }}
                    />
                  )}
                </div>
              </div>
            )}
            {activeStep === 2 && (
              <div>
                <div className="invoice-header" style={{ marginTop: '10px' }}>
                  <h3 className="text-black">Invoice Items</h3>
                  <button
                    type="button"
                    id="add-invoice-btn"
                    onClick={handleAddItem}
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
                {/* Desktop View */}
                <div className="hidden md:block">
                  <List sx={{ padding: 0, width: '100%' }}>
                    {items.map((item, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          gap: 1,
                          width: '100%',
                          paddingLeft: 0,
                          paddingRight: 0,
                          display: 'flex',
                          flexWrap: 'nowrap',
                        }}
                      >
                        <Autocomplete
                          options={invoiceTypes}
                          getOptionLabel={(option) => option.type}
                          value={invoiceTypes.find((t) => t.type === item.itemName) || null}
                          onChange={(_, newValue) => handleItemChange(index, 'itemName', newValue?.type || '')}
                          renderInput={(params) => (
                            <TextField {...params} label="Category" variant="outlined" fullWidth />
                          )}
                          sx={{ minWidth: 120 }}
                          disabled={!selectedProperty}
                        />
                        <TextField
                          label="Description"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          sx={{ minWidth: 120 }}
                        />
                        <FormControl>
                          <OutlinedInput
                            id={`outlined-adornment-amount-${index}`}
                            value={item.amount}
                            onChange={(e) => handleItemChange(index, 'amount', parseFloat(e.target.value) || 0)}
                            type="number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            startAdornment={<InputAdornment position="start">PHP</InputAdornment>}
                            sx={{ minWidth: 80 }}
                          />
                        </FormControl>
                        <button className="invoice-item-button" onClick={() => handleRemoveItem(index)} style={{ marginLeft: '8px' }}>
                          <i className="fas fa-trash"></i>
                        </button>
                        <button className="invoice-item-button" onClick={() => handleCopyItem(index)} style={{ marginLeft: '8px' }}>
                          <i className="fas fa-copy"></i>
                        </button>
                      </ListItem>
                    ))}
                  </List>
                </div>
                {/* Mobile View */}
                <div className="md:hidden">
                  <List sx={{ padding: 0, width: '100%' }}>
                    {items.map((item, index) => (
                      <ListItem
                        key={index}
                        className="w-full flex flex-col gap-2"
                        disableGutters
                        sx={{ paddingTop: '10px', paddingBottom: 0 }}
                      >
                        {/* Row 1: Category + Amount + Trash */}
                        <div className="flex flex-row w-full gap-2">
                          <div className="flex-1">
                            <Autocomplete
                              options={invoiceTypes}
                              getOptionLabel={(option) => option.type}
                              value={invoiceTypes.find((t) => t.type === item.itemName) || null}
                              onChange={(_, newValue) =>
                                handleItemChange(index, 'itemName', newValue?.type || '')
                              }
                              renderInput={(params) => (
                                <TextField {...params} label="Category" variant="outlined" fullWidth />
                              )}
                              disabled={!selectedProperty}
                            />
                          </div>

                          <div className="flex-1">
                            <FormControl fullWidth>
                              <OutlinedInput
                                id={`outlined-adornment-amount-${index}`}
                                value={item.amount}
                                onChange={(e) => handleItemChange(index, 'amount', parseFloat(e.target.value) || 0)}
                                type="number"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                startAdornment={<InputAdornment position="start">PHP</InputAdornment>}
                              />
                            </FormControl>
                          </div>
                          <button
                            className="invoice-item-button mt-[5px] ml-2"
                            onClick={() => handleRemoveItem(index)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>

                        {/* Row 2: Description + Copy */}
                        <div className="flex flex-row w-full gap-2">
                          <TextField
                            label="Description"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            className="flex-1 min-w-[120px]"
                          />
                          <button
                            className="invoice-item-button mt-[5px] ml-2"
                            onClick={() => handleCopyItem(index)}
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                        </div>
                      </ListItem>
                    ))}
                  </List>
                </div>
              </div>
            )}
            <React.Fragment>
              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 5, pb: 1 }}>
                <button className="stepper-button" disabled={activeStep === 0} onClick={handleBack}>
                  Back
                </button>
                <Box sx={{ flex: '1 1 auto' }} />
                <Tooltip title={!isStepCompleted(activeStep) ? "Please complete this step before proceeding" : ""}>
                  <span>
                    <button
                      className="stepper-button"
                      onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
                      disabled={!isStepCompleted(activeStep)}
                    >
                      {activeStep === steps.length - 1 ? "Finish" : "Next"}
                    </button>
                  </span>
                </Tooltip>
              </Box>
            </React.Fragment>
          </div>
          {error && <p>{error}</p>}
        </div>
      )}
    </div>
  );
};

export default ChargeInvoiceForm;
